// ---------------------------------------------------------------------------
// Perfis de matrícula
//
// Uma tarefa recorrente pode declarar para quais perfis de cliente ela é
// sugerida. A regra só guia: o sistema sugere e a pessoa sempre confirma —
// nada é matriculado sozinho.
// A regra fica em `tasks.enrollment_rule` e tem três eixos:
//
//   { regimes: ['Simples Nacional'], activities: ['Comércio'], payroll: ['Com folha'] }
//
// `regimes` é o eixo obrigatório — sem ele a tarefa simplesmente não tem regra
// e não é sugerida para ninguém (é escolhida cliente a cliente).
// `activities` e `payroll` são recortes opcionais: array vazio significa "não
// restringe", e não "não serve para ninguém".
// ---------------------------------------------------------------------------

export const REGIMES = [
  'Simples Nacional',
  'Lucro Presumido',
  'Lucro Real',
  'MEI',
  'Doméstica',
  'Profissional Liberal',
];

export const ACTIVITIES = ['Comércio', 'Serviços'];

export const PAYROLL = ['Com folha', 'Sem folha'];

const toArray = (value) => (Array.isArray(value) ? value : []);

// Normaliza a regra gravada. Aceita o formato antigo (só `regimes`), que é o
// que está no banco hoje, sem precisar de migração de dados.
export const getRule = (task) => {
  const rule = (task && task.enrollment_rule) || null;

  return {
    regimes: toArray(rule && rule.regimes),
    activities: toArray(rule && rule.activities),
    payroll: toArray(rule && rule.payroll),
  };
};

export const hasRule = (task) => getRule(task).regimes.length > 0;

// Uma regra só restringe atividade ou folha se o eixo tiver sido preenchido.
export const restrictsBeyondRegime = (task) => {
  const rule = getRule(task);
  return rule.activities.length > 0 || rule.payroll.length > 0;
};

// Os perfis que o departamento usa na prática: regime x atividade x folha.
// A Doméstica não tem esses recortes — é um perfil só. O Profissional Liberal
// presta serviço por definição, então só se desdobra em com/sem folha.
const buildProfiles = () => {
  const activityOptions = [
    { key: 'comercio', label: 'comércio', activities: ['Comércio'] },
    { key: 'servicos', label: 'serviços', activities: ['Serviços'] },
    { key: 'ambos', label: 'comércio e serviços', activities: ['Comércio', 'Serviços'] },
  ];
  const payrollOptions = [
    { key: 'com-folha', label: 'com folha', payroll: 'Com folha' },
    { key: 'sem-folha', label: 'sem folha', payroll: 'Sem folha' },
  ];

  const profiles = [];

  ['Simples Nacional', 'Lucro Presumido', 'Lucro Real', 'MEI'].forEach((regime) => {
    const regimeKey = regime.toLowerCase().replace(/\s+/g, '-');

    activityOptions.forEach((activity) => {
      payrollOptions.forEach((payroll) => {
        profiles.push({
          key: regimeKey + '/' + activity.key + '/' + payroll.key,
          label: regime.toLowerCase() + ' - ' + activity.label + ' - ' + payroll.label,
          regime: regime,
          activities: activity.activities,
          payroll: payroll.payroll,
        });
      });
    });
  });

  payrollOptions.forEach((payroll) => {
    profiles.push({
      key: 'profissional-liberal/' + payroll.key,
      label: 'profissional liberal - ' + payroll.label,
      regime: 'Profissional Liberal',
      activities: ['Serviços'],
      payroll: payroll.payroll,
    });
  });

  profiles.push({
    key: 'domestica',
    label: 'doméstica',
    regime: 'Doméstica',
    activities: [],
    payroll: null,
  });

  return profiles;
};

export const PROFILES = buildProfiles();

export const getProfile = (key) => {
  const found = PROFILES.filter((profile) => profile.key === key);
  return found.length > 0 ? found[0] : null;
};

// Agrupa os perfis por regime para o Select renderizar em blocos, como na
// lista que o departamento escreveu.
export const getProfileGroups = () => {
  const groups = [];

  PROFILES.forEach((profile) => {
    const existing = groups.filter((group) => group.regime === profile.regime);
    if (existing.length > 0) {
      existing[0].profiles.push(profile);
      return;
    }
    groups.push({ regime: profile.regime, profiles: [profile] });
  });

  return groups;
};

// Casamento entre a tarefa e o perfil escolhido. Eixo vazio na regra passa
// direto; o perfil "comércio e serviços" casa com quem exige qualquer um dos
// dois.
export const taskMatchesProfile = (task, profile) => {
  if (!profile || !hasRule(task)) {
    return false;
  }

  const rule = getRule(task);

  if (rule.regimes.indexOf(profile.regime) === -1) {
    return false;
  }

  if (rule.activities.length > 0 && profile.activities.length > 0) {
    const matched = profile.activities.filter(
      (activity) => rule.activities.indexOf(activity) > -1
    );
    if (matched.length === 0) {
      return false;
    }
  }

  if (rule.payroll.length > 0 && profile.payroll) {
    if (rule.payroll.indexOf(profile.payroll) === -1) {
      return false;
    }
  }

  return true;
};

// Texto curto da regra, para explicar no tooltip por que a tarefa entrou.
// Dentro de um eixo vale "ou"; entre eixos vale "e" — e é assim que o texto
// sai, para ninguém precisar adivinhar.
export const describeRule = (task) => {
  const rule = getRule(task);

  if (rule.regimes.length === 0) {
    return 'Sem regra de matrícula';
  }

  const parts = [rule.regimes.join(' ou ')];

  // Todas as atividades marcadas é o mesmo que nenhuma: não recorta.
  if (rule.activities.length > 0 && rule.activities.length < ACTIVITIES.length) {
    parts.push(rule.activities.join(' ou '));
  }
  if (rule.payroll.length > 0) {
    parts.push(rule.payroll.join(' ou '));
  }

  return parts.join(' e ');
};

const joinOr = (items) => {
  if (items.length <= 1) {
    return items.join('');
  }
  return items.slice(0, -1).join(', ') + ' ou ' + items[items.length - 1];
};

// A regra lida em voz alta, para o formulário da tarefa mostrar enquanto a
// pessoa monta. Eixo vazio vira "qualquer", que é o que ele significa.
export const explainRule = (rule) => {
  const regimes = toArray(rule && rule.regimes);
  const activities = toArray(rule && rule.activities);
  const payroll = toArray(rule && rule.payroll);

  if (regimes.length === 0) {
    return null;
  }

  const activityText = (activities.length === 0 || activities.length === ACTIVITIES.length)
    ? 'qualquer atividade'
    : 'atividade de ' + joinOr(activities.map((item) => item.toLowerCase()));

  let payrollText = 'com ou sem folha';
  if (payroll.length === 1) {
    payrollText = payroll[0] === 'Com folha' ? 'só com folha de pagamento' : 'só sem folha de pagamento';
  }

  return 'Sugerida para o cliente de ' + joinOr(regimes) + ', de ' + activityText + ', ' + payrollText + '.';
};

// ---------------------------------------------------------------------------
// Perfil sugerido pelo cadastro
//
// Escolher o perfil na mão, num Select de 25 opções, a cada abertura da aba
// Tarefas era trabalho que o cadastro já tinha feito: o regime está em
// `type_taxation`, a folha está em `n_staffs`/`type_staff` e a atividade dá
// para ler dos CNAEs. O que a derivação faz é propor — nunca aplicar sozinha —
// e dizer em que dado se baseou, porque cada eixo tem uma confiança diferente.
// ---------------------------------------------------------------------------

// Seção G da CNAE (divisões 45 a 47) é comércio; o resto do que estes
// escritórios atendem cai em serviços. Indústria (divisões 10 a 33) não tem
// caixa própria aqui — vira serviços com confiança baixa e o aviso junto.
const COMMERCE_DIVISIONS = ['45', '46', '47'];

const isIndustryDivision = (division) => {
  const number = parseInt(division, 10);
  return number >= 10 && number <= 33;
};

export const getCnaeDivisions = (customer) => {
  const raw = (customer && customer.cnaes) || '';

  return String(raw)
    .split(',')
    .map((cnae) => String(cnae).replace(/\D/g, ''))
    .filter((digits) => digits.length >= 2)
    .map((digits) => digits.substring(0, 2));
};

// Folha: quem tem funcionário declarado tem folha, e isso é dado duro. O zero
// é que é ambíguo — pode ser "não tem" ou "ninguém preencheu" —, então a
// sugestão continua sendo "sem folha", mas anunciada como palpite.
// Folha: o sinal mais forte é a tarefa FOLHA DE PAGAMENTO — quem está nela,
// ou tem funcionários lançados nela, tem folha. `payroll` vem do backend:
// { assigned, employees, employees_due_date } (null se o escritório não usa
// a tarefa). Sem ele, cai no cadastro.
export const PAYROLL_TASK_NAME = 'FOLHA DE PAGAMENTO';

const monthYear = (date) => {
  const parts = String(date || '').split('-');
  return parts.length >= 2 ? parts[1] + '/' + parts[0] : '';
};

export const suggestPayroll = (customer, payroll) => {
  if (payroll) {
    const employees = parseInt(payroll.employees, 10);
    if (!isNaN(employees) && employees > 0) {
      const when = monthYear(payroll.employees_due_date);
      return {
        key: 'com-folha',
        confidence: 'high',
        reason: employees + (employees === 1 ? ' funcionário' : ' funcionários') +
          ' na ' + PAYROLL_TASK_NAME + (when ? ' (' + when + ')' : ''),
      };
    }
    if (payroll.assigned) {
      return { key: 'com-folha', confidence: 'high', reason: 'cliente na tarefa ' + PAYROLL_TASK_NAME };
    }
  }

  const staffs = customer && customer.n_staffs;
  const typeStaff = (customer && customer.type_staff) || '';
  const count = parseInt(staffs, 10);

  if (!isNaN(count) && count > 0) {
    return {
      key: 'com-folha',
      confidence: 'high',
      reason: count + (count === 1 ? ' funcionário' : ' funcionários') + ' no cadastro',
    };
  }

  if (String(typeStaff).trim().length > 0) {
    return {
      key: 'com-folha',
      confidence: 'low',
      reason: 'sem número de funcionários, mas o tipo está preenchido (' + typeStaff + ')',
    };
  }

  if (payroll && !payroll.assigned) {
    return {
      key: 'sem-folha',
      confidence: 'high',
      reason: 'fora da tarefa ' + PAYROLL_TASK_NAME + ' e sem funcionários no cadastro',
    };
  }

  return {
    key: 'sem-folha',
    confidence: 'low',
    reason: 'nenhum funcionário no cadastro',
  };
};

export const suggestActivity = (customer) => {
  const divisions = getCnaeDivisions(customer);

  if (divisions.length === 0) {
    return { key: null, confidence: 'none', reason: 'nenhum CNAE cadastrado' };
  }

  const commerce = divisions.filter((division) => COMMERCE_DIVISIONS.indexOf(division) > -1);
  const industry = divisions.filter(isIndustryDivision);
  const others = divisions.filter(
    (division) => COMMERCE_DIVISIONS.indexOf(division) === -1 && !isIndustryDivision(division)
  );

  if (commerce.length > 0 && (others.length > 0 || industry.length > 0)) {
    return { key: 'ambos', confidence: 'high', reason: 'CNAEs de comércio e de serviços' };
  }

  if (commerce.length > 0) {
    return { key: 'comercio', confidence: 'high', reason: 'CNAE de comércio' };
  }

  if (industry.length > 0 && others.length === 0) {
    return {
      key: 'servicos',
      confidence: 'low',
      reason: 'CNAE de indústria, que não tem opção própria aqui',
    };
  }

  return { key: 'servicos', confidence: 'high', reason: 'CNAE de serviços' };
};

// Regimes que não se desdobram em atividade e folha: a Doméstica é perfil
// único. O Profissional Liberal só se desdobra em folha.
const SINGLE_PROFILE_REGIMES = { 'Doméstica': 'domestica' };
const PAYROLL_ONLY_REGIMES = { 'Profissional Liberal': 'profissional-liberal' };

export const suggestProfile = (customer, payrollSignal) => {
  const regime = (customer && customer.type_taxation) || null;

  if (!regime) {
    return { key: null, blocked: 'regime', reasons: ['o cadastro não diz o regime tributário'] };
  }

  if (SINGLE_PROFILE_REGIMES[regime]) {
    return {
      key: SINGLE_PROFILE_REGIMES[regime],
      confidence: 'high',
      reasons: ['regime ' + regime + ' no cadastro'],
    };
  }

  if (PAYROLL_ONLY_REGIMES[regime]) {
    const payrollOnly = suggestPayroll(customer, payrollSignal);
    return {
      key: PAYROLL_ONLY_REGIMES[regime] + '/' + payrollOnly.key,
      confidence: payrollOnly.confidence,
      reasons: ['regime ' + regime + ' no cadastro', payrollOnly.reason],
    };
  }

  const regimeKey = regime.toLowerCase().replace(/\s+/g, '-');
  const hasProfiles = PROFILES.filter((profile) => profile.regime === regime).length > 0;

  if (!hasProfiles) {
    return {
      key: null,
      blocked: 'regime',
      reasons: ['não há perfis montados para o regime ' + regime],
    };
  }

  const payroll = suggestPayroll(customer, payrollSignal);
  const activity = suggestActivity(customer);
  const reasons = ['regime ' + regime + ' no cadastro', payroll.reason];

  if (!activity.key) {
    // Falta só a atividade: em vez de chutar, devolve os três caminhos que
    // sobraram para a tela oferecer como atalho.
    return {
      key: null,
      blocked: 'activity',
      payrollKey: payroll.key,
      candidates: PROFILES
        .filter((profile) => profile.regime === regime)
        .filter((profile) => profile.key.indexOf('/' + payroll.key) > -1)
        .map((profile) => profile.key),
      reasons: reasons.concat([activity.reason]),
    };
  }

  const lowest = (payroll.confidence === 'low' || activity.confidence === 'low') ? 'low' : 'high';

  return {
    key: regimeKey + '/' + activity.key + '/' + payroll.key,
    confidence: lowest,
    reasons: reasons.concat([activity.reason]),
  };
};

// ---------------------------------------------------------------------------
// Casamento entre a tarefa e o cliente
//
// Três respostas, não duas: 'in', 'out' e 'unknown'. O 'unknown' é o caso em
// que a tarefa recorta por atividade ou folha e o cliente ainda não tem perfil
// gravado — quem chama decide se pergunta, se ignora ou se avisa, mas ninguém
// finge que sabe.
// ---------------------------------------------------------------------------

export const ENROLL_IN = 'in';
export const ENROLL_OUT = 'out';
export const ENROLL_UNKNOWN = 'unknown';

export const getCustomerProfile = (customer) => (
  (customer && customer.enrollment_profile) ? getProfile(customer.enrollment_profile) : null
);

export const matchesCustomer = (task, customer) => {
  if (!hasRule(task)) {
    return ENROLL_OUT;
  }

  const profile = getCustomerProfile(customer);

  if (profile) {
    return taskMatchesProfile(task, profile) ? ENROLL_IN : ENROLL_OUT;
  }

  const rule = getRule(task);
  const regime = (customer && customer.type_taxation) || null;

  if (!regime || rule.regimes.indexOf(regime) === -1) {
    return ENROLL_OUT;
  }

  return restrictsBeyondRegime(task) ? ENROLL_UNKNOWN : ENROLL_IN;
};

// Quantos clientes esta regra alcança hoje — o retorno que faltava para quem
// escreve a regra no cadastro da tarefa e para o painel da matriz.
//
// `rows` traz cada cliente com a situação dele, para a tela abrir a lista
// inteira e não só o número:
//   enrolled — a regra pega e ele já está na tarefa
//   missing  — a regra pega e ele ainda não está
//   in       — a regra pega (sem os vínculos em mãos para dizer se está)
//   unknown  — regime certo, mas sem perfil gravado para decidir o recorte
//   outside  — está na tarefa, mas a regra não o pega
export const REACH_ENROLLED = 'enrolled';
export const REACH_MISSING = 'missing';
export const REACH_IN = 'in';
export const REACH_UNKNOWN = 'unknown';
export const REACH_OUTSIDE = 'outside';

export const getRuleReach = (task, customers, isEnrolled) => {
  const reach = { in: 0, unknown: 0, enrolled: 0, missing: 0, outside: 0, rows: [] };

  (customers || []).forEach((customer) => {
    const status = matchesCustomer(task, customer);
    const enrolled = isEnrolled ? isEnrolled(customer) : false;

    if (status === ENROLL_UNKNOWN) {
      reach.unknown++;
      reach.rows.push({ customer, status: REACH_UNKNOWN, enrolled });
      return;
    }
    if (status !== ENROLL_IN) {
      if (enrolled) {
        reach.outside++;
        reach.rows.push({ customer, status: REACH_OUTSIDE, enrolled });
      }
      return;
    }

    reach.in++;

    if (!isEnrolled) {
      reach.rows.push({ customer, status: REACH_IN, enrolled });
    } else if (enrolled) {
      reach.enrolled++;
      reach.rows.push({ customer, status: REACH_ENROLLED, enrolled });
    } else {
      reach.missing++;
      reach.rows.push({ customer, status: REACH_MISSING, enrolled });
    }
  });

  return reach;
};

// Quem conta como cliente para efeito de matrícula. Mesma régua que a matriz
// Clientes x Tarefas usa, num lugar só, porque o número que a tela do cadastro
// mostra tem de ser o mesmo que a matriz depois aplica.
export const getActiveCustomers = (customers) => (customers || []).filter(
  (customer) => customer.enabled && customer.inactived_at === null && customer.is_customer
);

// ---------------------------------------------------------------------------
// Regra do cliente
//
// O cliente descreve a si mesmo nos mesmos três eixos da regra da tarefa, mas
// com um valor só em regime e folha: { regime, activities, payroll }. Grava no
// mesmo `enrollment_profile` de sempre — os chips só montam e desmontam a chave.
// ---------------------------------------------------------------------------

export const emptyChoice = () => ({ regime: null, activities: [], payroll: null });

export const choiceFromProfile = (key) => {
  const profile = getProfile(key);
  if (!profile) {
    return emptyChoice();
  }
  return {
    regime: profile.regime,
    activities: profile.activities.slice(),
    payroll: profile.payroll || null,
  };
};

const PAYROLL_KEYS = { 'Com folha': 'com-folha', 'Sem folha': 'sem-folha' };

// Profissional Liberal presta serviço; Doméstica não tem atividade nem folha.
export const choiceLocks = (regime) => ({
  activities: regime === 'Doméstica' || regime === 'Profissional Liberal',
  payroll: regime === 'Doméstica',
});

export const normalizeChoice = (choice) => {
  if (choice.regime === 'Doméstica') {
    return { regime: 'Doméstica', activities: [], payroll: null };
  }
  if (choice.regime === 'Profissional Liberal') {
    return { regime: choice.regime, activities: ['Serviços'], payroll: choice.payroll };
  }
  return choice;
};

export const profileKeyFromChoice = (choice) => {
  const c = normalizeChoice(choice || emptyChoice());
  if (!c.regime) {
    return null;
  }
  if (c.regime === 'Doméstica') {
    return 'domestica';
  }
  const payrollKey = PAYROLL_KEYS[c.payroll];
  if (!payrollKey) {
    return null;
  }
  if (c.regime === 'Profissional Liberal') {
    return 'profissional-liberal/' + payrollKey;
  }
  const has = (activity) => c.activities.indexOf(activity) > -1;
  let activityKey = null;
  if (has('Comércio') && has('Serviços')) {
    activityKey = 'ambos';
  } else if (has('Comércio')) {
    activityKey = 'comercio';
  } else if (has('Serviços')) {
    activityKey = 'servicos';
  }
  if (!activityKey) {
    return null;
  }
  const key = c.regime.toLowerCase().replace(/\s+/g, '-') + '/' + activityKey + '/' + payrollKey;
  return getProfile(key) ? key : null;
};

// O que falta escolher para a regra do cliente fechar.
export const missingInChoice = (choice) => {
  const c = normalizeChoice(choice || emptyChoice());
  const missing = [];
  if (!c.regime) {
    return ['regime'];
  }
  const locks = choiceLocks(c.regime);
  if (!locks.activities && c.activities.length === 0) {
    missing.push('atividade');
  }
  if (!locks.payroll && !c.payroll) {
    missing.push('folha');
  }
  return missing;
};

// Quais tarefas com regra este cliente recebe. Tarefa sem regra é manual e
// fica de fora da conta.
export const TASK_HAS = 'has';
export const TASK_MISSING = 'missing';
export const TASK_EXTRA = 'extra';

export const tasksForProfile = (tasks, profile) => {
  const result = { has: 0, missing: 0, extra: 0, manual: 0, rows: [] };

  (tasks || []).forEach((task) => {
    if (task.disabled_at) {
      return;
    }
    if (!hasRule(task)) {
      result.manual++;
      return;
    }
    const matches = profile ? taskMatchesProfile(task, profile) : false;
    const assigned = !!task.is_assigned;

    if (matches && assigned) {
      result.has++;
      result.rows.push({ task, status: TASK_HAS });
    } else if (matches) {
      result.missing++;
      result.rows.push({ task, status: TASK_MISSING });
    } else if (assigned) {
      result.extra++;
      result.rows.push({ task, status: TASK_EXTRA });
    }
  });

  return result;
};

// ── Grupos de tarefas ───────────────────────────────────────────────────────
//
// Um grupo junta tarefas irmãs (TFE janeiro, TFE fevereiro...). A regra do
// grupo diz quem entra nele; a cobertura diz em quantas filhas o cliente
// precisa estar. Grupo sem regra mas com cobertura vale para todo cliente.

export const COVERAGE_ANY = 'any';
export const COVERAGE_ONE = 'one';
export const COVERAGE_ALL = 'all';

export const COVERAGES = [
  { key: null, label: 'Livre', help: 'Só organiza: liga e desliga as filhas de uma vez, sem exigir nada.' },
  { key: COVERAGE_ANY, label: 'Pelo menos uma', help: 'Todo cliente do grupo precisa estar em uma ou mais filhas.' },
  { key: COVERAGE_ONE, label: 'Só uma', help: 'As filhas são alternativas: o cliente fica em exatamente uma.' },
  { key: COVERAGE_ALL, label: 'Todas', help: 'O cliente do grupo deve estar em todas as filhas.' },
];

export const coverageLabel = (coverage) => {
  const found = COVERAGES.filter((c) => c.key === (coverage || null))[0];
  return found ? found.label : 'Livre';
};

export const GROUP_OK = 'ok';
export const GROUP_PENDING = 'pending';
export const GROUP_OVER = 'over';
export const GROUP_EXTRA = 'extra';
export const GROUP_NONE = 'none';

// Filhas de um grupo que contam: as ativas e as desativadas onde o cliente
// ainda está (para dar como desligar).
export const groupChildren = (group, tasks) => (tasks || [])
  .filter((task) => task.group_id === group.id && (!task.disabled_at || task.is_assigned))
  // Na ordem do calendário (TFE janeiro, fevereiro...); sem data, pelo nome.
  .sort((a, b) => String(a.first_date || '').localeCompare(String(b.first_date || ''))
    || String(a.name).localeCompare(String(b.name)));

// O grupo vale para este cliente? Com regra, depende do perfil (null quando
// o perfil ainda não foi escolhido); sem regra, vale para todos se o grupo
// exige alguma cobertura, e para ninguém em particular se é livre.
export const groupApplies = (group, profile) => {
  if (hasRule(group)) {
    return profile ? taskMatchesProfile(group, profile) : null;
  }
  return group.coverage ? true : false;
};

export const groupStatus = (group, children, applies) => {
  const on = children.filter((task) => task.is_assigned).length;
  const total = children.length;
  let state = GROUP_NONE;

  if (applies) {
    if (group.coverage === COVERAGE_ONE) {
      state = on === 1 ? GROUP_OK : on > 1 ? GROUP_OVER : GROUP_PENDING;
    } else if (group.coverage === COVERAGE_ALL) {
      state = on === total ? GROUP_OK : GROUP_PENDING;
    } else if (group.coverage === COVERAGE_ANY) {
      state = on > 0 ? GROUP_OK : GROUP_PENDING;
    } else {
      state = on > 0 ? GROUP_OK : GROUP_NONE;
    }
  } else if (applies === false && hasRule(group) && on > 0) {
    state = GROUP_EXTRA;
  }

  return { on, total, state };
};

// O que a coverage pede, em uma frase, para mostrar ao lado do grupo.
export const coverageSentence = (group) => {
  if (group.coverage === COVERAGE_ANY) return 'precisa de pelo menos uma';
  if (group.coverage === COVERAGE_ONE) return 'precisa de exatamente uma';
  if (group.coverage === COVERAGE_ALL) return 'pede todas'
  return 'livre';
};

// ── Pendências de regra ─────────────────────────────────────────────────────
//
// Tarefa: tem regra (a própria ou a do grupo), foi marcada como "não possui
// regra" de propósito, ou ainda falta decidir. Cliente: todo cliente ativo
// precisa de regra — sem ela, o sistema não tem o que sugerir.

export const TASK_RULE_SET = 'set';
export const TASK_RULE_NONE = 'none';
export const TASK_RULE_MISSING = 'missing';

// "Não possui regra" fica gravado na própria regra, como { none: true }.
export const isMarkedNoRule = (task) => !!(task && task.enrollment_rule && task.enrollment_rule.none) && !hasRule(task);

export const NO_RULE_HELP = 'A tarefa não depende do tipo de cliente (regime, atividade ou folha), '
  + 'então o sistema não a sugere para ninguém: ela é ligada cliente a cliente, na Etapa 2 da aba Regra. '
  + 'Marcada assim, sai da lista de tarefas sem regra.';

export const MISSING_RULE_HELP = 'O sistema ainda não sabe para quem sugerir esta tarefa. Defina quem recebe '
  + '(regime, atividade, folha) ou marque "Não possui regra" se ela for escolhida cliente a cliente.';

export const taskRuleStatus = (task) => {
  if (hasRule(task)) return TASK_RULE_SET;
  const group = task && task.group;
  if (group && (hasRule(group) || group.coverage)) return TASK_RULE_SET;
  if (isMarkedNoRule(task)) return TASK_RULE_NONE;
  return TASK_RULE_MISSING;
};

// Cliente que conta como pendente: ativo (habilitado e não inativado) e sem
// regra gravada.
export const isActiveCustomer = (customer) => !!(customer && customer.enabled && !customer.inactived_at && !customer.deleted_at);
export const customerNeedsRule = (customer) => isActiveCustomer(customer) && !customer.enrollment_profile;

export const CUSTOMER_RULE_HELP = 'Todo cliente precisa de regra: é com ela (regime, atividade e folha) '
  + 'que o sistema sugere as tarefas que ele deve receber. Você confere e confirma; sem regra, não há sugestão.';
